/**
 * Production: booking + payment subdomains (see src/lib/env.ts).
 * Dev on localhost only: /booking and /payment on the same origin.
 */

import {
  DEFAULT_BOOKING_HOST,
  DEFAULT_PAYMENT_HOST,
  envBookingHost,
  envPaymentHost,
  envQuoteEmailBannerUrl,
} from './env';
import { sanitizeEticketNumber } from './skyluxReceiptIds';

const BOOKING_PREFIXES = ['booking.'];
const PAYMENT_PREFIXES = ['payment.'];

export type AppSurface = 'admin' | 'booking' | 'payment';

export function getHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.toLowerCase();
}

export function detectAppSurface(): AppSurface {
  const h = getHostname();
  if (BOOKING_PREFIXES.some((p) => h.startsWith(p))) return 'booking';
  if (PAYMENT_PREFIXES.some((p) => h.startsWith(p))) return 'payment';
  return 'admin';
}

function isDevHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
}

/** Production booking/payment hostnames for links in emails and quotes. */
export function resolvePublicQuoteHosts(): { bookingHost: string; paymentHost: string } {
  const envBooking = envBookingHost();
  const envPayment = envPaymentHost();
  if (envBooking && envPayment) {
    return { bookingHost: envBooking, paymentHost: envPayment };
  }

  const h = getHostname();
  if (h.startsWith('admin.')) {
    const base = h.slice('admin.'.length);
    return { bookingHost: `booking.${base}`, paymentHost: `payment.${base}` };
  }

  return { bookingHost: DEFAULT_BOOKING_HOST, paymentHost: DEFAULT_PAYMENT_HOST };
}

function productionPublicUrl(host: string, quoteId: string, extraParams?: URLSearchParams): string {
  const params = new URLSearchParams({ q: quoteId });
  if (extraParams) {
    extraParams.forEach((value, key) => params.set(key, value));
  }
  const qs = params.toString();
  return `https://${host}/?${qs}`;
}

/** Public absolute URL for the booking (view ticket) subdomain. */
export function buildBookingQuoteUrl(quoteId: string): string {
  if (typeof window === 'undefined') {
    const { bookingHost } = resolvePublicQuoteHosts();
    return productionPublicUrl(bookingHost, quoteId);
  }

  const { protocol } = window.location;
  const port = window.location.port ? `:${window.location.port}` : '';
  const h = getHostname();

  if (isDevHost(h)) {
    const origin = `${protocol}//${h}${port}`;
    return `${origin}/booking?q=${encodeURIComponent(quoteId)}`;
  }

  const { bookingHost } = resolvePublicQuoteHosts();
  return productionPublicUrl(bookingHost, quoteId);
}

/** Public e-ticket page — `/ticket/{13-digit e-ticket number}` on the booking host. */
export function buildViewTicketUrl(eTicketNumber: string): string {
  const ticket = sanitizeEticketNumber(eTicketNumber);
  if (!ticket) {
    return '#';
  }

  if (typeof window === 'undefined') {
    const { bookingHost } = resolvePublicQuoteHosts();
    return `https://${bookingHost}/ticket/${ticket}`;
  }

  const { protocol } = window.location;
  const port = window.location.port ? `:${window.location.port}` : '';
  const h = getHostname();

  if (isDevHost(h)) {
    const origin = `${protocol}//${h}${port}`;
    return `${origin}/ticket/${ticket}`;
  }

  const { bookingHost } = resolvePublicQuoteHosts();
  return `https://${bookingHost}/ticket/${ticket}`;
}

export type PaymentLinkOptions = {
  /** @deprecated Invoice-only React page removed; static checkout ignores this. */
  view?: 'invoice';
  /** @deprecated Use default checkout URL. */
  action?: 'pay';
};

/** Public absolute URL for the payment subdomain (checkout by default, or invoice). */
export function buildPaymentUrl(quoteId: string, options?: PaymentLinkOptions): string {
  const extra = new URLSearchParams();
  if (options?.view === 'invoice') extra.set('view', 'invoice');

  if (typeof window === 'undefined') {
    const { paymentHost } = resolvePublicQuoteHosts();
    return productionPublicUrl(paymentHost, quoteId, extra);
  }

  const { protocol } = window.location;
  const port = window.location.port ? `:${window.location.port}` : '';
  const h = getHostname();

  if (isDevHost(h)) {
    const origin = `${protocol}//${h}${port}`;
    const params = new URLSearchParams({ q: quoteId });
    if (options?.view === 'invoice') params.set('view', 'invoice');
    return `${origin}/payment/index.html?${params.toString()}`;
  }

  const { paymentHost } = resolvePublicQuoteHosts();
  return productionPublicUrl(paymentHost, quoteId, extra);
}

function normalizeHost(value: string): string {
  return value.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

/** Crypto / promo banner image in quote emails. */
export function buildQuoteEmailBannerUrl(): string {
  const override = envQuoteEmailBannerUrl();
  if (override) return override;
  const { paymentHost } = resolvePublicQuoteHosts();
  return `https://${normalizeHost(paymentHost)}/banner.jpg`;
}
